import { useState, useCallback, useEffect } from "react";
import { useApi } from "../containers/ApiContainer";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { HoldingAreaService } from "../services/HoldingAreaService";
import { HoldingAreaEntry, Tenant } from "../types";

export const useHoldingArea = () => {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const [entries, setEntries] = useState<HoldingAreaEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const service = new HoldingAreaService(postgrest);

  /**
   * Fetch active holding area entries
   */
  const refresh = useCallback(async () => {
    if (!activeFBO?.id) {
      setEntries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await service.getActiveEntries(activeFBO.id);
      setEntries(data);
    } catch (err) {
      setError(err as Error);
      console.error("Failed to fetch holding area entries:", err);
    } finally {
      setLoading(false);
    }
  }, [activeFBO?.id, postgrest]);

  /**
   * Add a tenant to the holding area
   */
  const addTenant = useCallback(
    async (tenant: Tenant | Tenant[]) => {
      if (!activeFBO?.id) {
        throw new Error("No active FBO");
      }

      const tenants = Array.isArray(tenant) ? tenant : [tenant];

      try {
        setError(null);
        // Add each tenant to the holding area
        await Promise.all(
          tenants.map((t) => service.addToHoldingArea(t.id, activeFBO.id))
        );
        // Refresh the list
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [activeFBO?.id, refresh]
  );

  /**
   * Remove a tenant from the holding area
   */
  const removeTenant = useCallback(
    async (tenantId: string) => {
      try {
        setError(null);
        await service.removeFromHoldingArea(tenantId);
        // Refresh the list
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  /**
   * Assign a tenant from the holding area to a hangar
   */
  const assignTenant = useCallback(
    async (tenantId: string) => {
      try {
        setError(null);
        await service.assignFromHoldingArea(tenantId);
        // Refresh the list
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  /**
   * Check if a tenant is in the holding area
   */
  const isInHoldingArea = useCallback(
    async (tenantId: string): Promise<boolean> => {
      try {
        return await service.isInHoldingArea(tenantId);
      } catch (err) {
        console.error("Failed to check holding area status:", err);
        return false;
      }
    },
    []
  );

  // Load initial data
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    entries,
    loading,
    error,
    addTenant,
    removeTenant,
    assignTenant,
    isInHoldingArea,
    refresh,
  };
};
